import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SendIcon } from './icons';
import type { ChatMessage } from '../providers/types';
import { RichMessage } from './chat/RichMessage';

/** Hard upper bound on chat message length. Enforced in the UI via counter + disabled send. */
export const CHAT_MESSAGE_MAX = 500;
/** Threshold at which the character counter becomes visible. */
const CHAT_COUNTER_THRESHOLD = 450;

/**
 * Returns true when a message contains rich content (product cards, video, or media attachments).
 * Used to apply wider bubble max-width for rich messages (max-w-[90%] vs max-w-[70%]).
 */
const isRichMessage = (msg: ChatMessage): boolean =>
  msg.attributes?.type === 'product' ||
  (msg.attributes?.shared as Record<string, unknown> | undefined)?.type === 'product' ||
  msg.attributes?.type === 'video' ||
  !!(msg.attachedMedia && msg.attachedMedia.length > 0);

interface ChatOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  messages: ChatMessage[];
  onSendMessage: (text: string) => void;
  /** When undefined or empty, the advisor is not yet bound — show a neutral connecting header. */
  advisorName?: string;
  advisorTitle?: string;
  advisorImage?: string;
  onProductClick?: (productId: number, variantId?: number) => void;
}

export function ChatOverlay({
  isOpen,
  messages,
  onSendMessage,
  advisorName,
  advisorTitle,
  advisorImage,
  onProductClick,
}: ChatOverlayProps) {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const trimmedLength = inputValue.length;
  const isOverLimit = trimmedLength > CHAT_MESSAGE_MAX;
  const showCounter = trimmedLength >= CHAT_COUNTER_THRESHOLD;
  const sendDisabled = !inputValue.trim() || isOverLimit;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isOverLimit) return;
    onSendMessage(inputValue);
    setInputValue('');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="absolute inset-10 bg-brand-cream/[0.98] backdrop-blur-sm flex flex-col shadow-2xl border border-brand-gold/20 z-20"
        >
          {/* Chat Header */}
          <div className="px-8 py-6 border-b border-brand-gold/20">
            {advisorName ? (
              <div className="flex items-center gap-4">
                {/* Avatar — object-contain so non-square brand logos render
                    in full instead of being cropped by the circular clip. */}
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-brand-gold flex-shrink-0 bg-brand-gold/10 flex items-center justify-center">
                  {advisorImage ? (
                    <img
                      src={advisorImage}
                      alt={advisorName}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full bg-brand-gold/20 flex items-center justify-center">
                      <span className="text-lg font-serif text-brand-gold">
                        {advisorName.charAt(0)}
                      </span>
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-sm text-brand-navy font-serif tracking-wide">
                    {advisorName}
                  </p>
                  {advisorTitle && (
                    <p className="text-xs text-brand-navy/60 font-sans">
                      {advisorTitle}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full border-2 border-brand-gold/40 flex-shrink-0 bg-brand-gold/10 flex items-center justify-center">
                  <span className="text-brand-gold/60 text-lg font-serif">·</span>
                </div>
                <p className="text-sm text-brand-navy/70 font-serif tracking-wide italic">
                  Connecting you with your specialist…
                </p>
              </div>
            )}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-8 space-y-6">
            {messages.map((message, index) => {
              const isOwn = message.isLocal;
              return (
                <motion.div
                  key={message.id || index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`${isRichMessage(message) ? 'max-w-[90%]' : 'max-w-[70%]'} px-6 py-4 rounded-sm shadow-sm ${
                      isOwn
                        ? 'bg-brand-navy text-white'
                        : 'bg-white border border-brand-gold/20 text-brand-navy'
                    }`}
                  >
                    <RichMessage message={message} isOwn={isOwn} onProductClick={onProductClick} />
                  </div>
                </motion.div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="px-8 py-6 border-t border-brand-gold/20">
            <div className="flex gap-3">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type your message..."
                aria-invalid={isOverLimit}
                className={`flex-1 px-6 py-4 bg-white border text-brand-navy text-sm placeholder:text-brand-navy/40 focus:outline-none transition-colors font-sans ${
                  isOverLimit
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-brand-gold/20 focus:border-brand-gold'
                }`}
              />
              <button
                type="submit"
                disabled={sendDisabled}
                className="px-6 py-4 bg-brand-navy text-white hover:bg-brand-gold transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <SendIcon size={20} strokeWidth={1.5} />
              </button>
            </div>
            {showCounter && (
              <div
                data-testid="chat-char-counter"
                aria-live="polite"
                className={`mt-2 text-xs font-sans text-right ${
                  isOverLimit ? 'text-red-500' : 'text-brand-navy/60'
                }`}
              >
                {trimmedLength} / {CHAT_MESSAGE_MAX}
                {isOverLimit && <span className="ml-2">Message is too long</span>}
              </div>
            )}
          </form>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
